import React, { useMemo, useState } from 'react';
import {
  Form,
  Select,
  Input,
  InputNumber,
  Upload,
  Button,
  Card,
  Table,
  Space,
  notification,
  Divider,
} from 'antd';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { API } from '@/services/api.service';
import AopFindingsAlert from './AopFindingsAlert';


export default function PeriodicReportPanel({ companySizes, getLocalizedLabel }) {
  const { t, i18n } = useTranslation(['forms', 'messages', 'tables']);
  const p = (key, options) =>
    t(`financialStatements.periodic.${key}`, { ns: 'tables', ...options });
  const [form] = Form.useForm();
  const [bbFile, setBbFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [generated, setGenerated] = useState(null);

  const { data: periodsData } = useQuery({
    queryKey: ['fs-form-periods'],
    queryFn: () => API.financialStatements.getFormPeriodsEnum(),
  });

  const formPeriods = (periodsData?.data || []).filter(
    (p) => p.key !== 'ANNUAL'
  );

  const previewMutation = useMutation({
    mutationFn: (formData) =>
      API.financialStatements.uploadAndPreviewPeriodic(formData),
    onSuccess: (resp) => {
      setPreview(resp.data);
      setGenerated(null);
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: p('previewDone'),
        placement: 'bottomRight',
      });
    },
    onError: (err) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: err?.response?.data?.message || err.message,
        placement: 'bottomRight',
      });
    },
  });

  const generateMutation = useMutation({
    mutationFn: (payload) => API.financialStatements.generatePeriodicReport(payload),
    onSuccess: (resp) => {
      setGenerated(resp.data);
      notification.success({
        message: t('success', { ns: 'messages' }),
        description: p('excelSaved', { fileName: resp.data.fileName }),
        placement: 'bottomRight',
      });
    },
    onError: (err) => {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: err?.response?.data?.message || err.message,
        placement: 'bottomRight',
      });
    },
  });

  const handlePreview = async () => {
    try {
      const values = await form.validateFields();
      if (!bbFile) {
        notification.warning({
          message: t('error', { ns: 'messages' }),
          description: p('bbRequired'),
          placement: 'bottomRight',
        });
        return;
      }
      const formData = new FormData();
      formData.append('currentYearFile', bbFile);
      formData.append('companySize', values.companySize);
      formData.append('year', values.year);
      formData.append('formPeriod', values.formPeriod);
      formData.append('periodIndex', values.periodIndex);
      if (values.companyName) formData.append('companyName', values.companyName);
      previewMutation.mutate(formData);
    } catch {
      // form validation already shows messages
    }
  };

  const handleGenerate = async () => {
    if (!preview) return;
    const values = await form.validateFields();
    generateMutation.mutate({
      companyName: values.companyName || null,
      companySize: values.companySize,
      year: parseInt(values.year, 10),
      formPeriod: values.formPeriod,
      periodIndex: parseInt(values.periodIndex, 10),
      language: values.language,
      // Used by perStatement sizes (VELIKO) to pick which statement to emit as
      // its own file. Ignored by combined sizes (MALO), which emit both.
      formType: values.formType,
      accountBalances: preview.accountBalances,
      bilansStanja: preview.bilansStanja,
      bilansUspeha: preview.bilansUspeha,
    });
  };

  const handleDownload = async () => {
    if (!generated?.statementId) return;
    try {
      await API.financialStatements.downloadPeriodicExcel(generated.statementId);
    } catch (err) {
      notification.error({
        message: t('error', { ns: 'messages' }),
        description: err?.response?.data?.message || err.message,
        placement: 'bottomRight',
      });
    }
  };

  const aopColumns = useMemo(
    () => [
      { title: p('aop'), dataIndex: 'aopCode', key: 'aopCode', width: 80 },
      {
        title: p('currentYear'),
        dataIndex: 'currentYear',
        key: 'currentYear',
        align: 'right',
        render: (v) => (v == null ? '—' : Number(v).toLocaleString('sr-RS')),
      },
      {
        title: p('previousYear'),
        dataIndex: 'previousYear',
        key: 'previousYear',
        align: 'right',
        render: (v) => (v == null ? '—' : Number(v).toLocaleString('sr-RS')),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language]
  );

  const selectedPeriod = Form.useWatch('formPeriod', form);
  const periodEnum = formPeriods.find((p) => p.key === selectedPeriod);

  return (
    <div className="grid gap-6">
      <Card title={p('title')}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            year: new Date().getFullYear(),
            formPeriod: 'QUARTERLY',
            periodIndex: 1,
            language: i18n.language === 'sr' ? 'sr' : 'en',
            formType: 'BILANS_USPEHA',
          }}
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Form.Item
              label={p('companyName')}
              name="companyName"
              tooltip={p('companyNameHint')}
            >
              <Input placeholder="Acme d.o.o." />
            </Form.Item>

            <Form.Item
              label={p('companySize')}
              name="companySize"
              rules={[{ required: true }]}
            >
              <Select
                placeholder={p('companySizePlaceholder')}
                options={companySizes?.map((s) => ({
                  value: s.key,
                  label: getLocalizedLabel
                    ? getLocalizedLabel(s)
                    : s.labelEn || s.key,
                }))}
              />
            </Form.Item>

            <Form.Item label={p('year')} name="year" rules={[{ required: true }]}>
              <InputNumber min={2020} max={2100} className="w-full" />
            </Form.Item>

            <Form.Item
              label={p('periodType')}
              name="formPeriod"
              rules={[{ required: true }]}
            >
              <Select
                options={formPeriods.map((p) => ({
                  value: p.key,
                  label: i18n.language === 'sr' ? p.labelSr : p.labelEn,
                }))}
              />
            </Form.Item>

            <Form.Item
              label={
                periodEnum
                  ? p(`periodIndex.${periodEnum.key}`, { defaultValue: p('periodIndex.default') })
                  : p('periodIndex.default')
              }
              name="periodIndex"
              rules={[{ required: true }]}
            >
              <InputNumber
                min={periodEnum?.indexRange?.[0] || 1}
                max={periodEnum?.indexRange?.[1] || 12}
                className="w-full"
              />
            </Form.Item>

            <Form.Item label={p('language')} name="language">
              <Select
                options={[
                  { value: 'sr', label: 'Srpski' },
                  { value: 'en', label: 'English' },
                ]}
              />
            </Form.Item>

            <Form.Item
              label={p('statement')}
              name="formType"
              tooltip={p('statementHint')}
            >
              <Select
                options={[
                  { value: 'BILANS_USPEHA', label: p('bilansUspeha') },
                  { value: 'BILANS_STANJA', label: p('bilansStanja') },
                ]}
              />
            </Form.Item>
          </div>

          <Divider />

          <Form.Item label={p('bbFile')} required>
            <Upload
              beforeUpload={(file) => {
                setBbFile(file);
                return false;
              }}
              onRemove={() => setBbFile(null)}
              maxCount={1}
              accept=".xls,.xlsx"
            >
              <Button shape="round" icon={<UploadOutlined />}>
                {p('selectBb')}
              </Button>
            </Upload>
          </Form.Item>

          <Space>
            <Button
              type="primary"
              shape="round"
              loading={previewMutation.isPending}
              onClick={handlePreview}
              disabled={!bbFile}
            >
              {p('preview')}
            </Button>
            <Button
              type="primary"
              shape="round"
              loading={generateMutation.isPending}
              onClick={handleGenerate}
              disabled={!preview}
            >
              {p('generate')}
            </Button>
            <Button
              shape="round"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              disabled={!generated?.statementId}
            >
              {p('download')}
            </Button>
          </Space>
        </Form>
      </Card>

      {preview && (
        <Card
          title={p('previewTitle', {
            bs: preview.bilansStanja.length,
            bu: preview.bilansUspeha.length,
          })}
          size="small"
        >
          <AopFindingsAlert
            warnings={preview.validation?.warnings}
            className="mb-3"
          />
          <p className="mb-3 text-xs text-gray-500">{p('previewHint')}</p>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <h4 className="mb-2 font-semibold">{p('bilansStanja')}</h4>
              <Table
                size="small"
                columns={aopColumns}
                dataSource={preview.bilansStanja}
                rowKey="aopCode"
                pagination={{ pageSize: 20 }}
                scroll={{ y: 400 }}
              />
            </div>
            <div>
              <h4 className="mb-2 font-semibold">{p('bilansUspeha')}</h4>
              <Table
                size="small"
                columns={aopColumns}
                dataSource={preview.bilansUspeha}
                rowKey="aopCode"
                pagination={{ pageSize: 20 }}
                scroll={{ y: 400 }}
              />
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
